import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function CRSPanel({ project, reload, catalog }) {
  const id = project.id;
  const [srcCrs, setSrcCrs] = useState("EPSG:4210");
  const rep = project.crs_report;
  const hasOrig = !!project.boundary_original;

  const correct = async (action, src) => {
    try {
      const { data } = await api.post(`/projects/${id}/boundary/correct`, {
        action, src_crs: src || srcCrs, dst_crs: "EPSG:4326",
      });
      toast.success("Coordinate correction applied");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!hasOrig)
    return <p className="text-sm text-muted-foreground">Upload or set a boundary first - CRS validation runs against the original coordinates.</p>;

  return (
    <div className="space-y-4">
      <div className="text-xs rounded-md p-3 border border-border bg-accent/40">
        The original uploaded coordinates are preserved. Corrections never overwrite the source file - both original and corrected boundaries are stored.
      </div>

      <div>
        <p className="overline mb-2">Datum / CRS Conversion</p>
        <div className="flex gap-2">
          <Select value={srcCrs} onValueChange={setSrcCrs}>
            <SelectTrigger className="h-9 text-xs" data-testid="crs-src-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(catalog?.crs_options || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => correct("convert")} data-testid="crs-convert">Convert → WGS 84</Button>
        </div>
      </div>

      <div>
        <p className="overline mb-2">Quick Corrections</p>
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" variant="outline" onClick={() => correct("convert", "EPSG:4210")} data-testid="crs-arc1960">Arc 1960 → WGS 84</Button>
          <Button size="sm" variant="outline" onClick={() => correct("swap_xy")} data-testid="crs-swap">Swap Lat/Lon</Button>
          <Button size="sm" variant="outline" onClick={() => correct("repair")} data-testid="crs-repair">Repair Geometry</Button>
          <Button size="sm" variant="outline" onClick={() => correct("keep")} data-testid="crs-keep">Keep Original</Button>
        </div>
      </div>

      {rep && (
        <div data-testid="crs-report">
          <p className="overline mb-2">CRS Correction Report</p>
          <table className="w-full text-xs border border-border rounded-md overflow-hidden">
            <tbody className="font-mono">
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Method</td><td className="px-3 py-2">{rep.method}</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Source CRS</td><td className="px-3 py-2">{rep.src_crs}</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Target CRS</td><td className="px-3 py-2">{rep.dst_crs}</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Original Centre</td><td className="px-3 py-2">{rep.original_centroid?.y}, {rep.original_centroid?.x}</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Corrected Centre</td><td className="px-3 py-2">{rep.corrected_centroid?.y}, {rep.corrected_centroid?.x}</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Coordinate Shift</td><td className="px-3 py-2">{rep.shift_m} m</td></tr>
              <tr className="border-b border-border"><td className="px-3 py-2 text-muted-foreground">Confidence</td><td className="px-3 py-2">{rep.confidence}</td></tr>
              <tr><td className="px-3 py-2 text-muted-foreground">Status</td><td className="px-3 py-2 text-priority-low">{rep.status}</td></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
